import { DEVICE_SCOPED_LOCAL_STORAGE_KEYS } from "./deviceScopedStorageKeys.ts";

/**
 * Written once a document has been answered by an authenticated API route, and
 * removed by every wipe. It is how a later auth rejection knows there was a
 * session here to end: a visitor who never signed in has nothing to wipe and
 * must not be bounced through a reload. Because the wipe removes it before the
 * document is torn down, the next document finds no marker and a rejection
 * there cannot reload again — that, not a path check, is the loop guard.
 *
 * It persists across documents on purpose. A session that expires while the
 * tab is closed still leaves the account's storage behind, and the rejection on
 * the next visit is the only chance to clear it.
 */
export const AUTHENTICATED_SESSION_MARKER_KEY = "okthx.session.authenticated";

// The in-memory half of the marker, for a browser whose storage is disabled:
// a document that was authenticated still tears down on rejection, and the
// flag dies with the document, so there is still no loop.
let documentAuthenticated = false;

/** Note that this document has held an authenticated session. Idempotent. */
export function recordAuthenticatedSession(): void {
  if (documentAuthenticated) return;
  documentAuthenticated = true;
  try {
    localStorage.setItem(AUTHENTICATED_SESSION_MARKER_KEY, "1");
  } catch {
    // Storage disabled: the in-memory flag alone gates this document.
  }
}

/**
 * Whether an authenticated session existed here to end, clearing the record as
 * it answers so the same session is only ever ended once.
 */
export function takeAuthenticatedSession(): boolean {
  const inMemory = documentAuthenticated;
  documentAuthenticated = false;
  let persisted = false;
  try {
    persisted = localStorage.getItem(AUTHENTICATED_SESSION_MARKER_KEY) !== null;
    if (persisted) localStorage.removeItem(AUTHENTICATED_SESSION_MARKER_KEY);
  } catch {
    // Unreadable or unremovable: treat as absent, so a reload can never be
    // triggered by a marker that would survive it.
    persisted = false;
  }
  return inMemory || persisted;
}

/** The keys a storage area currently holds, read through the standard API. */
function storedKeys(storage: Storage): string[] {
  const keys: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (key !== null) keys.push(key);
  }
  return keys;
}

/**
 * Delete every trace of the account from browser storage: all of localStorage
 * except the device-scoped allowlist, and all of this tab's sessionStorage.
 * Deleting is the default; a key survives only by being named in
 * {@link DEVICE_SCOPED_LOCAL_STORAGE_KEYS}, so a key added anywhere in the app
 * is cleared at logout without anyone remembering to list it (CASA 6.6.1).
 *
 * localStorage is removed key by key rather than with `clear()`, because it is
 * shared by every tab on the origin and they sign out together. Clearing would
 * mean deleting the allowlisted key and writing it back, and a second tab
 * running that same read-clear-restore can interleave: it reads while the first
 * tab has cleared but not yet restored, sees nothing to keep, and its own clear
 * then drops the value the first tab had just put back. Never removing the key
 * leaves no such window. Removing one key at a time is also monotonic, so no
 * tab can observe an account value reappear mid-wipe.
 *
 * sessionStorage is scoped to this tab alone, so nothing can race there and it
 * is cleared outright.
 *
 * Each backend is wiped in its own try block: private mode and blocked storage
 * can disable one independently of the other, and a failure in one must not
 * leave the other holding account data. For the same reason each localStorage
 * key is removed in its own try block, so one failure cannot stop the sweep.
 *
 * Storage only. The query cache and the module-scope atoms holding drafts are
 * only reachable by tearing the document down, which {@link endSession} and
 * {@link endSessionInPlace} do — a caller that keeps the document must know it
 * is keeping that state.
 */
export function clearClientStorage(): void {
  const kept = new Set<string>(DEVICE_SCOPED_LOCAL_STORAGE_KEYS);
  let keys: string[] = [];
  try {
    keys = storedKeys(localStorage);
  } catch {
    // Unreadable: nothing here can be deleted, and sessionStorage still is.
  }
  for (const key of keys) {
    if (kept.has(key)) continue;
    try {
      localStorage.removeItem(key);
    } catch {
      // One key that won't go must not shield the ones after it.
    }
  }
  try {
    sessionStorage.clear();
  } catch {
    // Blocked storage here must not undo the localStorage sweep above.
  }
}

/**
 * End the session in this document: wipe storage, then load `destination` as a
 * new document so the query cache, atoms and React tree go with it. The wipe
 * comes first so nothing account-scoped is left for the next document to read.
 */
export function endSession(destination: string): void {
  clearClientStorage();
  window.location.assign(destination);
}

/** {@link endSession} for a document that should reload where it is. */
export function endSessionInPlace(): void {
  clearClientStorage();
  window.location.reload();
}

/**
 * Whether a `storage` event from another tab reports a sign-out there.
 *
 * The signal is the session marker being removed, which every wipe does: it is
 * not allowlisted, so the sweep deletes it, and the rejection path takes it
 * first. A null key — a wholesale `clear()` — is accepted too, so that a tab
 * still hears a sign-out if anything else ever empties the store.
 */
function isSignOutEvent(event: StorageEvent): boolean {
  if (event.key === null) return true;
  return event.key === AUTHENTICATED_SESSION_MARKER_KEY && event.newValue === null;
}

/**
 * Make this tab follow a sign-out performed in another tab of the same origin.
 * localStorage is shared, so the other tab's wipe already covered it; what this
 * tab alone can reach is its own sessionStorage and its own in-memory state.
 * A tab that held the session reloads, which drops both; a tab that never did
 * (the sign-in page, a public link) only clears its sessionStorage.
 *
 * Returns the uninstaller.
 */
export function installCrossTabSignOut(): () => void {
  const onStorage = (event: StorageEvent) => {
    if (!isSignOutEvent(event)) return;
    if (documentAuthenticated) {
      endSessionInPlace();
      return;
    }
    try {
      sessionStorage.clear();
    } catch {
      // Storage disabled: nothing was held here to clear.
    }
  };
  window.addEventListener("storage", onStorage);
  return () => window.removeEventListener("storage", onStorage);
}
